'use client';

import React, { useMemo } from 'react';

import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useUser } from 'global/UserContext';
import { useIframeCommunication } from 'hooks/useIframeCommunication';
import { buildThemeParams } from 'microfrontends/config/theme';
import { API_URLS } from 'utils/apiUrlBuilder';

interface GeneralMarketplaceProps {
  categoryId: number;
}

const GeneralMarketplace: React.FC<GeneralMarketplaceProps> = ({ categoryId }) => {
  const { user } = useUser();
  const userId = user?.id;
  const theme = useThemeContext();

  const themeParams = useMemo(() => buildThemeParams(theme), [theme]);

  const getOrganizationUuid = () => {
    try {
      const orgUuid = localStorage.getItem('x-organization-uuid');
      if (!orgUuid) {
        console.warn('Organization UUID not found in localStorage');
        return '';
      }
      return orgUuid;
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return '';
    }
  };

  const baseUrl = useMemo(() => {
    const orgUuid = getOrganizationUuid();
    return `${API_URLS.MARKET_PLATFORM}/showcase/${categoryId}?organization_uuid=${orgUuid}&userId=${userId}`;
  }, [userId]);

  const iframeUrl = useMemo(() => `${baseUrl}&${themeParams}`, [baseUrl, themeParams]);

  const { iframeRef } = useIframeCommunication(iframeUrl);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
      }}
    >
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        style={{
          flex: 1,
          minHeight: '1000px',
          border: 'none',
        }}
        width="100%"
        title="Marketplace - NextJS"
      />
    </div>
  );
};

export default GeneralMarketplace;

/*
SISTEMA DE TEMA - MARKETPLACE

Este componente usa URL parameters para passar o tema para o iframe do Marketplace.
Para garantir que o tema seja mantido mesmo após navegação interna no Marketplace,
o iframe deve implementar o sistema de localStorage:

1. No iframe do Marketplace, adicione:
   ```typescript
   import { initializeTheme } from 'microfrontends/utils/themeUtils';

   useEffect(() => {
     const cleanup = initializeTheme();
     return cleanup;
   }, []);
   ```

2. Use as CSS variables no CSS do Marketplace:
   ```css
   .marketplace-container {
     background-color: var(--bg-primary);
     color: var(--text-primary);
   }
   ```

Isso garante consistência visual mesmo com navegação interna!
*/
